import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, PackageSearch, CreditCard, Ship, FileText, Handshake } from "lucide-react";
import { Reveal } from "../Reveal";
import { useLang } from "../../i18n/LanguageContext";
import { ASSETS } from "../../lib/site";

const barIcons = [PackageSearch, CreditCard, Ship, FileText, Handshake];
const barReds = [false, true, false, true, false];

export const CaseStudy = () => {
  const { t } = useLang();
  return (
    <section data-testid="case-study-section" className="bg-sinovar-soft py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="rounded-[2rem] border border-black/[0.05] bg-white p-6 md:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <span className="inline-block rounded-full bg-sinovar-blue/10 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-sinovar-blue">
                {t.caseStudy.featured}
              </span>
              <div className="mt-5 font-display text-2xl md:text-3xl font-bold tracking-tight text-sinovar-navy">
                {t.caseStudy.brand}
              </div>
              <div className="mt-4 h-0.5 w-10 rounded-full bg-sinovar-red" />
              <Reveal delay={0.05}>
                <h2 className="mt-5 font-display text-3xl md:text-[2.7rem] font-bold tracking-tight text-sinovar-navy leading-[1.08]">
                  {t.caseStudy.titleA}
                  <span className="text-sinovar-red">{t.caseStudy.titleB}</span>
                  {t.caseStudy.titleC}
                </h2>
              </Reveal>
              <p className="mt-5 max-w-xl text-[15px] md:text-base text-sinovar-navy/60 leading-relaxed">
                {t.caseStudy.desc}
              </p>
              <div className="mt-6 flex items-center gap-2.5 text-sm font-medium text-sinovar-navy/70">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-sinovar-blue/10 text-sinovar-blue">
                  <CalendarDays className="h-4 w-4" />
                </span>
                {t.caseStudy.label}
              </div>
              <Link
                to="/case-studies/lbs-store"
                data-testid="case-study-cta"
                className="group mt-7 inline-flex items-center gap-2 text-[15px] font-semibold text-sinovar-blue"
              >
                {t.caseStudy.cta}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden rounded-2xl shadow-xl shadow-sinovar-navy/10 aspect-[4/3]"
            >
              <img src={ASSETS.lbsProducts} alt="LBS Store products" className="h-full w-full object-cover" />
            </motion.div>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-y-6 gap-x-4 border-t border-black/[0.06] pt-8 md:divide-x md:divide-black/[0.06]">
            {t.caseStudy.serviceBar.map((s, i) => {
              const Icon = barIcons[i];
              const red = barReds[i];
              return (
                <div key={i} className="flex items-center gap-3 md:px-4 first:md:pl-0" data-testid={`case-service-${i}`}>
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${red ? "bg-sinovar-red/10 text-sinovar-red" : "bg-sinovar-blue/10 text-sinovar-blue"}`}>
                    <Icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <span className="text-[13px] font-semibold text-sinovar-navy/75 leading-tight">{s}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
